import { useState, useEffect, useRef } from 'react';
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from './contexts/ThemeContext';
import { LoginScreen } from './components/LoginScreen';
import { SuperAdminPortal } from './components/super-admin/SuperAdminPortal';
import { ClientPortal } from './components/client/ClientPortal';
import { LicenseExpiredScreen } from './components/LicenseExpiredScreen';
import { sessionStorage, authApi } from './utils/auth-api';
import { adminApi } from './utils/admin-api';
import { motion } from 'framer-motion';

const CACHE_VERSION = '2.1.0-session-stability';

const getSessionRole = (value: any) => value?.user?.role || value?.role;
const getSessionClientId = (value: any) => value?.user?.clientId || value?.clientId;

const shouldFetchClientData = (value: any) => {
  const role = getSessionRole(value);
  const clientId = getSessionClientId(value);
  const isExpired = value?.licenseExpired === true;
  return !!value?.token && role !== 'super_admin' && role !== 'admin_staff' && !isExpired && !!clientId;
};

const isAuthError = (error: any) => {
  const message = String(error?.message || '').toLowerCase();
  return (
    message.includes('401')
    || message.includes('403')
    || message.includes('unauthorized')
    || message.includes('forbidden')
    || message.includes('session expired')
    || message.includes('token')
  );
};

const migrateLocalCache = () => {
  try {
    const cacheVersion = localStorage.getItem('ems_cache_version');
    if (cacheVersion === CACHE_VERSION) {
      return;
    }

    // Remove only known transient keys; keep auth/session/theme and user data.
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith('temp_') || key.startsWith('cache_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    localStorage.setItem('ems_cache_version', CACHE_VERSION);
  } catch (error) {
    console.warn('⚠️ Cache migration skipped:', error);
  }
};

function AppContent() {
  const [session, setSession] = useState<any>(null);
  const [client, setClient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [clientLoadError, setClientLoadError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const clearAndResetToLogin = () => {
    sessionStorage.clear();
    setSession(null);
    setClient(null);
    setClientLoadError(null);
    setLoading(false);
  };

  const loadClientForSession = async (activeSession: any) => {
    const role = getSessionRole(activeSession);
    const clientId = getSessionClientId(activeSession);
    const isExpired = activeSession?.licenseExpired === true;

    if (role === 'super_admin' || role === 'admin_staff' || isExpired || !clientId) {
      setClientLoadError(null);
      return;
    }

    try {
      const clientData = await adminApi.getClient(activeSession.token, clientId);
      if (isMountedRef.current) {
        // Check if theme config changed
        const oldTheme = JSON.stringify(client?.themeConfig);
        const newTheme = JSON.stringify(clientData?.themeConfig);
        if (oldTheme !== newTheme) {
          console.log('🎨 Client theme config updated:', clientData?.themeConfig);
        }
        
        setClient(clientData);
        sessionStorage.save({ ...activeSession, client: clientData });
        setClientLoadError(null);
      }
    } catch (error: any) {
      console.error('❌ Failed to load client data:', error);
      if (!isMountedRef.current) return;

      if (isAuthError(error)) {
        clearAndResetToLogin();
        return;
      }

      setClientLoadError(error?.message || 'Failed to load client data.');
    }
  };

  useEffect(() => {
    if (!session || !shouldFetchClientData(session)) {
      return;
    }

    let disposed = false;

    const refreshClient = async () => {
      if (disposed || !isMountedRef.current) {
        return;
      }
      console.log('🔄 Polling client data for theme/config changes...');
      await loadClientForSession(session);
    };

    const handleWindowFocus = () => {
      console.log('👁️ Window focused - refreshing client data');
      refreshClient();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Tab visible - refreshing client data');
        refreshClient();
      }
    };

    // Keep config/theme sync without hammering API when many tabs are open.
    const interval = window.setInterval(refreshClient, 30000);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [session?.token, session?.user?.clientId, session?.clientId, session?.licenseExpired, session?.user?.role, session?.role]);

  console.log('🟢 AppContent rendered, loading:', loading, 'session:', !!session);

  useEffect(() => {
    console.log('🟢 Cleanup effect mounted');
    return () => {
      isMountedRef.current = false;
      console.log('🟢 Component unmounting');
    };
  }, []);

  // Initialize on mount - SIMPLE, no crashes
  useEffect(() => {
    console.log('🟢 Init effect running');
    
    let cancelled = false;
    isMountedRef.current = true;

    migrateLocalCache();

    const init = async () => {
      console.log('🚀 Starting app initialization');
      try {
        // Restore and verify saved session to avoid stale/broken auth states.
        const savedSession = sessionStorage.get();
        
        if (savedSession?.token) {
          try {
            const verifiedSession = await authApi.verifySession(savedSession.token);
            if (cancelled || !isMountedRef.current) {
              return;
            }

            // Keep storage in sync and preserve compatibility keys.
            sessionStorage.save(verifiedSession);
            setSession(verifiedSession);
            await loadClientForSession(verifiedSession);
            setLoading(false);
            return;
          } catch (verifyError) {
            console.warn('⚠️ Saved session verification failed, clearing session:', verifyError);
            if (!cancelled && isMountedRef.current) {
              clearAndResetToLogin();
            }
            return;
          }
        }
        
        // No saved session
        console.log('No saved session found - showing login');
        if (!cancelled && isMountedRef.current) {
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ Init error:', error);
        if (!cancelled && isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []); // Run ONLY on mount

  // Fetch client data if session exists but client data is missing
  useEffect(() => {
    if (!session || client) return;
    const role = getSessionRole(session);
    const clientId = getSessionClientId(session);
    const isExpired = session.licenseExpired === true;
    
    if (role === 'super_admin' || role === 'admin_staff' || isExpired || !clientId) return;

    console.log('📥 Fetching missing client data for:', clientId);
    
    let cancelled = false;
    
    const fetchClient = async () => {
      if (!cancelled && isMountedRef.current) {
        await loadClientForSession(session);
      }
    };
    
    fetchClient();

    return () => {
      cancelled = true;
    };
  }, [session]);

  const handleLogin = (newSession: any, clientData?: any) => {
    console.log('✅ Login successful', { 
      hasSession: !!newSession, 
      hasClient: !!clientData,
      sessionRole: newSession?.user?.role || newSession?.role,
      licenseExpired: newSession?.licenseExpired
    });
    
    // Just save what we got from login - don't re-verify
    sessionStorage.save(clientData ? { ...newSession, client: clientData } : newSession);
    
    if (isMountedRef.current) {
      setLoading(false);
      setSession(newSession);
      setClientLoadError(null);
      
      // Set client if provided and license is active
      if (clientData && newSession?.licenseExpired !== true) {
        console.log('Setting client data from login');
        setClient(clientData);
        // Fetch latest client state after login so updated theme changes are reflected immediately.
        loadClientForSession(newSession);
      } else if (!clientData) {
        setClient(null);
        console.log('⚠️ No client data provided from login');
      }
    }
  };

  const handleLogout = async () => {
    console.log('🚪 Logout triggered');
    if (session) {
      try {
        await authApi.logout(session.token);
      } catch (error) {
        console.error('Logout error (non-blocking):', error);
      }
    }
    sessionStorage.clear();
    setSession(null);
    setClient(null);
    setClientLoadError(null);
    setLoading(false);
    // Don't need to reset initStartedRef - it will be reset on page reload
  };

  if (loading) {
    console.log('📍 Rendering loading state');
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (!session) {
    console.log('❌ No session - showing LoginScreen');
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <LoginScreen onLogin={handleLogin} onInitNeeded={() => {}} />
      </motion.div>
    );
  }

  // Log full session structure for debugging
  console.log('📋 Full session object:', {
    hasToken: !!session?.token,
    role: session?.user?.role || session?.role,
    licenseExpired: session?.licenseExpired,
    daysUntilExpiration: session?.daysUntilExpiration,
    hasUser: !!session?.user,
    sessionKeys: Object.keys(session || {})
  });

  // Render appropriate portal based on role
  const userRole = session?.user?.role || session?.role;
  const licenseExpired = session?.licenseExpired === true;

  console.log('🔍 App render state:', { userRole, licenseExpired, hasClient: !!client, sessionExists: !!session });

  if (userRole === 'super_admin' || userRole === 'admin_staff') {
    console.log('👑 Rendering SuperAdminPortal');
    return <SuperAdminPortal session={session} onLogout={handleLogout} />;
  }

  // CRITICAL: Check license expiration BEFORE attempting any portal rendering
  // If license is expired, show ONLY the expiration screen - nothing else
  if (licenseExpired) {
    console.log('🔴 CRITICAL: License is expired - ONLY showing LicenseExpiredScreen, not rendering anything else');
    return (
      <LicenseExpiredScreen
        clientName={session?.user?.email?.split('@')[0] || 'User'}
        clientEmail={session?.user?.email || ''}
        expiredAt={session?.licenseExpiresAt}
        onLogout={handleLogout}
      />
    );
  }

  // Only for active licenses - render client portal
  if ((userRole === 'admin' || userRole === 'supervisor' || userRole === 'operator' || userRole === 'client_admin')) {
    if (!client) {
      // Load client data before rendering portal
      console.log('⏳ Loading client data...');
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full"
          />
        </div>
      );
    }
    
    console.log('✅ Rendering ClientPortal with active license');
    if (clientLoadError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md w-full rounded-xl border border-border bg-card p-6 text-center space-y-4">
            <h2 className="text-xl font-semibold">Unable to load portal data</h2>
            <p className="text-sm text-muted-foreground">{clientLoadError}</p>
            <p className="text-xs text-muted-foreground">Please check your internet connection, or try again in a moment.</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => loadClientForSession(session)}
                className="px-4 py-2 bg-primary text-primary-foreground rounded"
              >
                Retry
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-muted text-foreground rounded"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      );
    }

    return <ClientPortal client={client} session={session} onLogout={handleLogout} />;
  }

  console.log('⚠️ WARNING: No matching portal for role:', userRole);
  // Fallback - should not reach here
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <p className="text-red-500 font-bold mb-4">Invalid state: role={userRole}, licenseExpired={licenseExpired}</p>
        <button
          onClick={handleLogout}
          className="px-4 py-2 bg-red-500 text-white rounded"
        >
          Logout
        </button>
      </div>
    </div>
  );
}

export default function App() {
  console.log('🚀 App component rendering');
  return (
    <ThemeProvider>
      <AppContent />
      <Toaster />
    </ThemeProvider>
  );
}
